/**
 * Normaliza resposta GET /api/v2/recommendations/metrics/store
 *
 * Formato esperado (ex.):
 * {
 *   metricas_geral: { quantidade_conversoes, soma_total_valor_conversoes, conversions, top_5_..., ... },
 *   metricas_periodo: { ... },
 *   template_analytics: { data: [{ granularity, data_points: [{ start, sent, delivered, read, clicked }] }] }
 * }
 *
 * Pode vir embrulhado em data: { data: { metricas_geral, ... } }
 */

function num(v) {
  if (v == null || v === '') return null
  const n = typeof v === 'string' ? parseFloat(v.replace(',', '.')) : Number(v)
  return Number.isNaN(n) ? null : n
}

/** Soma todos os `count` em `clicked` (cada entrada do array), conforme contrato da API */
function sumClickedForPoint(pt) {
  const clk = pt.clicked || []
  let total = 0
  for (let i = 0; i < clk.length; i += 1) {
    total += num(clk[i].count) || 0
  }
  return total
}

export function aggregateTemplateAnalytics(templateAnalytics) {
  if (!templateAnalytics || !templateAnalytics.data || !Array.isArray(templateAnalytics.data)) {
    return null
  }
  let sent = 0
  let delivered = 0
  let read = 0
  let clicked = 0
  for (let b = 0; b < templateAnalytics.data.length; b += 1) {
    const points = templateAnalytics.data[b].data_points || []
    for (let i = 0; i < points.length; i += 1) {
      const pt = points[i]
      sent += num(pt.sent) || 0
      delivered += num(pt.delivered) || 0
      read += num(pt.read) || 0
      clicked += sumClickedForPoint(pt)
    }
  }
  return {
    sent,
    delivered,
    read,
    clicked,
  }
}

/** Lista plana de pontos diários (timestamp em segundos) para gráficos */
export function flattenTemplateDailyPoints(templateAnalytics) {
  if (!templateAnalytics || !templateAnalytics.data || !Array.isArray(templateAnalytics.data)) {
    return []
  }
  const out = []
  for (let b = 0; b < templateAnalytics.data.length; b += 1) {
    const points = templateAnalytics.data[b].data_points || []
    for (let i = 0; i < points.length; i += 1) {
      const pt = points[i]
      const start = num(pt.start)
      if (start != null) {
        out.push({
          start,
          sent: num(pt.sent) || 0,
          delivered: num(pt.delivered) || 0,
          read: num(pt.read) || 0,
          clicked: sumClickedForPoint(pt),
        })
      }
    }
  }
  out.sort((a, b) => a.start - b.start)
  return out
}

function buildConversionsFromBlock(bloco) {
  const conv = bloco.conversions || {}
  const totalRevenue = num(conv.total_revenue) ?? num(bloco.soma_total_valor_conversoes)
  const totalPurchases = num(conv.total_purchases) ?? num(bloco.quantidade_conversoes)
  const conversionRate = num(conv.conversion_rate) ?? num(bloco.taxa_conversao)
  const avgOrder = num(conv.avg_order_value) ?? num(bloco.ticket_medio_conversoes)
  const totalClicks = num(conv.total_clicks)
  const ctr = num(conv.click_through_rate)

  return {
    total_revenue: totalRevenue,
    direct_revenue: num(conv.direct_revenue),
    incentive_revenue: num(conv.incentive_revenue),
    total_purchases: totalPurchases,
    direct_purchases: num(conv.direct_purchases),
    incentive_purchases: num(conv.incentive_purchases),
    conversion_rate: conversionRate,
    avg_order_value: avgOrder,
    total_clicks: totalClicks,
    click_through_rate: ctr,
  }
}

function buildSummaryFromBlock(bloco) {
  return {
    para_quantos_clientes: num(bloco.clientes_unicos_contactados),
    produtos_recomendados_total: num(bloco.total_produtos_unicos_recomendados),
    recomendacoes_criadas: num(bloco.total_produtos_recomendados),
    mensagens_enviadas: num(bloco.quantidade_mensagens_enviadas),
  }
}

/**
 * @param {Object} raw - resposta axios
 * @param {Object} options
 * @param {'geral'|'periodo'} options.escopo - qual bloco usar (lifetime vs período filtrado)
 */
export function normalizarMetricasPayload(raw, options = {}) {
  const { escopo = 'geral' } = options
  const root = raw && (raw.data != null ? raw.data : raw)
  if (!root || typeof root !== 'object') return null

  let bloco = null
  if (escopo === 'geral' && root.metricas_geral != null) {
    bloco = root.metricas_geral
  } else if (escopo === 'periodo' && root.metricas_periodo != null) {
    bloco = root.metricas_periodo
  } else if (root.metricas != null && typeof root.metricas === 'object') {
    bloco = root.metricas
  } else if (root.quantidade_conversoes != null || root.conversions != null) {
    bloco = root
  }

  if (!bloco || typeof bloco !== 'object') return null

  const conversions = buildConversionsFromBlock(bloco)
  const summary = buildSummaryFromBlock(bloco)

  const templateAnalytics = escopo === 'periodo'
    ? (root.template_analytics || bloco.template_analytics || null)
    : null
  const templateAggregates = templateAnalytics ? aggregateTemplateAnalytics(templateAnalytics) : null
  const templateDailyPoints = templateAnalytics ? flattenTemplateDailyPoints(templateAnalytics) : []

  let pedidosConvertidos = []
  if (escopo === 'periodo') {
    if (Array.isArray(bloco.pedidos_convertidos)) {
      pedidosConvertidos = bloco.pedidos_convertidos
    } else if (Array.isArray(root.pedidos_convertidos)) {
      pedidosConvertidos = root.pedidos_convertidos
    }
  }

  const historicoEnvios = escopo === 'periodo'
    ? (root.historico_envios ?? bloco.historico_envios ?? null)
    : null

  const base = {
    campaign: root.campaign || null,
    period: root.period || null,
    historico_conversoes:
      bloco.historico_conversoes
      ?? root.historico_conversoes
      ?? bloco.historico_semanal
      ?? null,
    top_5_produtos_recomendados:
      bloco.top_5_produtos_recomendados
      ?? root.top_5_produtos_recomendados
      ?? null,
    top_pedidos_recomendados: root.top_pedidos_recomendados ?? bloco.top_pedidos_recomendados ?? null,
    metricasRaw: bloco,
    templateAnalytics,
    templateAggregates,
    templateDailyPoints,
    pedidos_convertidos: pedidosConvertidos,
    historico_envios: historicoEnvios,
  }

  return {
    ...base,
    conversions,
    summary,
  }
}
